/* SawaKart listing (collection + search) = staging's /products behaviour on Shopify's native filters.

   - Filters, chips, sort and price update the results in place: the target URL is fetched through
     the Section Rendering API (?section_id=) and the [data-sk-region] parts are swapped, with
     history (pushState / popstate). Every control is a real link or GET form, so the page also
     works without JavaScript, and any failure falls back to a normal page load. A link to another
     path (a category) navigates normally: the template's other sections belong to the collection.
   - Infinite scroll (300px before the end) with the "Load more products" link as fallback.
   - Empire binds quick add / quick shop and lazy images to the cards through its section events
     (Sections in empire.js listens for shopify:section:unload/load on document). Around every swap
     or append the events are replayed on this element, so the new cards are bound like on page load.
   - Panel UI: collapsible groups, category tree, show all / less, search within a group, and on
     phones the Filters and Sort bottom sheets (<dialog>), the panel moving into the sheet. */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('sk-listing')) return;

  var DESKTOP = window.matchMedia('(min-width: 1024px)');
  var CACHE_MAX = 30;

  function isModified(event) {
    return event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  class SkListing extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.cache = new Map();
      this.controller = null;
      this.loadingMore = false;
      this.priceTimer = 0;

      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPop = this.onPop.bind(this);
      this.onMedia = this.onMedia.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPop);
      if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', this.onMedia);

      this.querySelectorAll('dialog[data-sk-sheet]').forEach((sheet) => {
        sheet.addEventListener('click', (event) => { if (event.target === sheet) this.closeSheet(sheet); });
        sheet.addEventListener('close', () => this.onSheetClosed(sheet));
      });

      try {
        history.replaceState(Object.assign({}, history.state, { skListing: true }), '');
      } catch (e) { /* history may be locked in the theme editor */ }
      this.observe();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('popstate', this.onPop);
      if (DESKTOP.removeEventListener) DESKTOP.removeEventListener('change', this.onMedia);
      if (this.io) this.io.disconnect();
      if (this.controller) this.controller.abort();
      clearTimeout(this.priceTimer);
      document.documentElement.classList.remove('sk-scroll-lock');
    }

    /* ---------- events ---------- */

    onClick(event) {
      var target = event.target;

      var groupToggle = target.closest('[data-sk-group-toggle]');
      if (groupToggle) {
        groupToggle.setAttribute('aria-expanded', groupToggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
        return;
      }

      var treeToggle = target.closest('[data-sk-ctoggle], [data-sk-ctoggle-row]');
      if (treeToggle) {
        this.toggleBranch(treeToggle.closest('.sk-cnode'));
        return;
      }

      var more = target.closest('[data-sk-more]');
      if (more) { this.showAll(more.closest('[data-sk-group-body]'), true); return; }
      var less = target.closest('[data-sk-less]');
      if (less) { this.showAll(less.closest('[data-sk-group-body]'), false); return; }

      var open = target.closest('[data-sk-open]');
      if (open) { this.openSheet(open.getAttribute('data-sk-open')); return; }

      var close = target.closest('[data-sk-close]');
      if (close) { this.closeSheet(close.closest('dialog')); return; }

      var moreLink = target.closest('[data-sk-more-link]');
      if (moreLink) {
        if (isModified(event)) return;
        event.preventDefault();
        this.loadMore();
        return;
      }

      var link = target.closest('a[data-sk-nav]');
      if (link) {
        if (isModified(event) || link.target === '_blank') return;
        var url = new URL(link.href, location.href);
        if (url.origin !== location.origin) return;
        if (link.hasAttribute('data-sk-close-sheet')) this.closeSheet(link.closest('dialog'));
        if (url.pathname !== location.pathname) return; /* another listing: normal navigation */
        event.preventDefault();
        this.navigate(url);
      }
    }

    onInput(event) {
      var target = event.target;
      if (target.matches('[data-sk-search]')) {
        this.searchGroup(target.closest('[data-sk-group-body]'), target.value);
      } else if (target.matches('[data-sk-price-input]')) {
        clearTimeout(this.priceTimer);
        var form = target.form;
        this.priceTimer = setTimeout(() => this.submitPrice(form), 500);
      }
    }

    onChange(event) {
      var select = event.target;
      if (!select.matches('[data-sk-sort]')) return;
      var option = select.options[select.selectedIndex];
      var href = option && option.getAttribute('data-url');
      if (href) this.navigate(new URL(href, location.href));
    }

    onSubmit(event) {
      var form = event.target;
      if (!form.matches('[data-sk-price]')) return;
      event.preventDefault();
      clearTimeout(this.priceTimer);
      this.submitPrice(form);
    }

    onPop(event) {
      if (!event.state || !event.state.skListing) return;
      this.navigate(new URL(location.href), { push: false });
    }

    onMedia(event) {
      if (event.matches) this.querySelectorAll('dialog[data-sk-sheet][open]').forEach((sheet) => this.closeSheet(sheet));
    }

    /* ---------- in-place navigation ---------- */

    submitPrice(form) {
      if (!form) return;
      var url = new URL(form.getAttribute('action') || location.pathname, location.href);
      var params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      url.search = params.toString();
      this.navigate(url);
    }

    async navigate(url, options) {
      var push = !options || options.push !== false;
      var href = url.toString();
      if (url.pathname !== location.pathname) { location.assign(href); return; }

      if (this.controller) this.controller.abort();
      var controller = new AbortController();
      this.controller = controller;
      this.classList.add('is-loading');

      try {
        var html = await this.fetchSection(href, controller.signal);
        var fresh = this.parse(html);
        if (!fresh) throw new Error('No listing in the response');
        var state = this.captureState();
        this.withEmpire(() => this.swap(fresh));
        this.restoreState(state);
        if (push) history.pushState({ skListing: true }, '', href);
        this.observe();
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        location.assign(href);
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.classList.remove('is-loading');
        }
      }
    }

    async fetchSection(href, signal) {
      var url = new URL(href, location.href);
      url.searchParams.set('section_id', this.sectionId);
      var key = url.toString();
      if (this.cache.has(key)) return this.cache.get(key);
      var response = await fetch(key, { signal: signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      var text = await response.text();
      this.cache.set(key, text);
      if (this.cache.size > CACHE_MAX) this.cache.delete(this.cache.keys().next().value);
      return text;
    }

    parse(html) {
      var listing = new DOMParser().parseFromString(html, 'text/html').querySelector('sk-listing');
      /* DOMParser reads <noscript> as markup: its fallback images would download with every update. */
      if (listing) listing.querySelectorAll('noscript').forEach((el) => el.remove());
      return listing;
    }

    /* Replace each [data-sk-region] with the fresh one (wherever it currently is: the panel may sit
       in the filters sheet). */
    swap(fresh) {
      fresh.querySelectorAll('[data-sk-region]').forEach((region) => {
        var name = region.getAttribute('data-sk-region');
        var current = this.querySelector('[data-sk-region="' + name + '"]');
        if (current) current.replaceWith(document.importNode(region, true));
      });
    }

    /* Empire (re)binds the cards: unload the old instance, change the DOM, load a new one. */
    withEmpire(mutate) {
      var detail = { sectionId: this.sectionId, skListing: true };
      this.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: detail }));
      try {
        mutate();
      } finally {
        this.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: detail }));
      }
    }

    /* ---------- keep the panel as the shopper left it ---------- */

    captureState() {
      var state = { groups: {}, card: 0, sheet: 0, focus: null };
      this.querySelectorAll('[data-sk-group]').forEach((group) => {
        var key = group.getAttribute('data-sk-group');
        var head = group.querySelector('[data-sk-group-toggle]');
        var search = group.querySelector('[data-sk-search]');
        var less = group.querySelector('[data-sk-less]');
        state.groups[key] = {
          open: head ? head.getAttribute('aria-expanded') !== 'false' : true,
          query: search ? search.value : '',
          all: !!(less && !less.hidden),
          branches: Array.prototype.map.call(group.querySelectorAll('.sk-ctree--children:not([hidden])'), (list) => this.branchKey(list))
        };
      });
      var card = this.querySelector('.sk-listing__card');
      var sheetBody = this.querySelector('[data-sk-panel-sheet]');
      state.card = card ? card.scrollTop : 0;
      state.sheet = sheetBody ? sheetBody.scrollTop : 0;
      var active = document.activeElement;
      if (active && this.contains(active) && active.name && active.matches('[data-sk-price-input]')) {
        state.focus = { name: active.name, start: null };
        try { state.focus.start = active.selectionStart; } catch (e) { /* number inputs */ }
      }
      return state;
    }

    restoreState(state) {
      Object.keys(state.groups).forEach((key) => {
        var saved = state.groups[key];
        var group = this.querySelector('[data-sk-group="' + CSS.escape(key) + '"]');
        if (!group) return;
        var head = group.querySelector('[data-sk-group-toggle]');
        if (head) head.setAttribute('aria-expanded', saved.open ? 'true' : 'false');
        var body = group.querySelector('[data-sk-group-body]');
        if (saved.all) this.showAll(body, true);
        var search = group.querySelector('[data-sk-search]');
        if (search && saved.query) {
          search.value = saved.query;
          this.searchGroup(body, saved.query);
        }
        saved.branches.forEach((branchKey) => {
          var list = this.findBranch(group, branchKey);
          if (list && list.hidden) this.toggleBranch(list.closest('.sk-cnode'), true);
        });
      });
      var card = this.querySelector('.sk-listing__card');
      if (card) card.scrollTop = state.card;
      var sheetBody = this.querySelector('[data-sk-panel-sheet]');
      if (sheetBody) sheetBody.scrollTop = state.sheet;
      if (state.focus) {
        var input = this.querySelector('[data-sk-price-input][name="' + CSS.escape(state.focus.name) + '"]');
        if (input) {
          input.focus({ preventScroll: true });
          var end = input.value.length;
          try { input.setSelectionRange(end, end); } catch (e) { /* number inputs */ }
        }
      }
    }

    branchKey(list) {
      var row = list.closest('.sk-cnode');
      var label = row && row.querySelector('.sk-crow__label');
      return label ? label.textContent.trim() : '';
    }

    findBranch(group, key) {
      var lists = group.querySelectorAll('.sk-ctree--children');
      for (var i = 0; i < lists.length; i++) {
        if (this.branchKey(lists[i]) === key) return lists[i];
      }
      return null;
    }

    /* ---------- panel controls ---------- */

    toggleBranch(node, forceOpen) {
      if (!node) return;
      var list = node.querySelector(':scope > .sk-ctree--children');
      if (!list) return;
      var open = forceOpen === true ? true : list.hidden;
      list.hidden = !open;
      var toggle = node.querySelector(':scope > .sk-crow-wrap > [data-sk-ctoggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        var label = toggle.getAttribute('aria-label') || '';
        toggle.setAttribute('aria-label', label.replace(/^(Expand|Collapse)/, open ? 'Collapse' : 'Expand'));
      }
    }

    showAll(body, all) {
      if (!body) return;
      body.querySelectorAll('[data-sk-extra]').forEach((item) => {
        var active = item.querySelector('.is-active');
        item.hidden = !all && !active;
      });
      var more = body.querySelector('[data-sk-more]');
      var less = body.querySelector('[data-sk-less]');
      if (more) more.hidden = all;
      if (less) less.hidden = !all;
    }

    searchGroup(body, query) {
      if (!body) return;
      var q = String(query || '').trim().toLowerCase();
      var items = body.querySelectorAll('[data-sk-list] > li');
      var shown = 0;
      var less = body.querySelector('[data-sk-less]');
      var all = !!(less && !less.hidden);
      items.forEach((item) => {
        var match;
        if (q) {
          match = (item.getAttribute('data-sk-label') || '').indexOf(q) !== -1;
        } else {
          match = !item.hasAttribute('data-sk-extra') || all || !!item.querySelector('.is-active');
        }
        item.hidden = !match;
        if (match) shown++;
      });
      var more = body.querySelector('[data-sk-more]');
      if (more) more.hidden = !!q || all;
      if (less) less.hidden = !!q || !all;
      var none = body.querySelector('[data-sk-nomatch]');
      if (none) none.hidden = !(q && shown === 0);
    }

    /* ---------- phone sheets ---------- */

    openSheet(name) {
      var sheet = this.querySelector('dialog[data-sk-sheet="' + name + '"]');
      if (!sheet || sheet.open || typeof sheet.showModal !== 'function') return;
      if (name === 'filters') {
        var panel = this.querySelector('[data-sk-region="panel"]');
        var slot = sheet.querySelector('[data-sk-panel-sheet]');
        if (panel && slot) slot.appendChild(panel);
      }
      sheet.showModal();
      document.documentElement.classList.add('sk-scroll-lock');
    }

    closeSheet(sheet) {
      if (sheet && sheet.open) sheet.close();
    }

    onSheetClosed(sheet) {
      if (sheet.getAttribute('data-sk-sheet') === 'filters') {
        var panel = this.querySelector('[data-sk-region="panel"]');
        var home = this.querySelector('[data-sk-panel-home]');
        if (panel && home && panel.parentNode !== home) home.appendChild(panel);
      }
      if (!this.querySelector('dialog[data-sk-sheet][open]')) document.documentElement.classList.remove('sk-scroll-lock');
    }

    /* ---------- infinite scroll ---------- */

    observe() {
      if (this.io) this.io.disconnect();
      var sentinel = this.querySelector('[data-sk-sentinel]');
      if (!sentinel || !this.querySelector('[data-sk-more-link]') || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '0px 0px 300px 0px' });
      this.io.observe(sentinel);
    }

    async loadMore() {
      var link = this.querySelector('[data-sk-more-link]');
      if (!link || this.loadingMore || this.controller) return;
      this.loadingMore = true;
      var loading = this.querySelector('[data-sk-loading]');
      link.hidden = true;
      if (loading) loading.hidden = false;
      var href = link.href;
      try {
        var fresh = this.parse(await this.fetchSection(href));
        /* A filter or sort changed meanwhile: these products belong to the old results. */
        if (!link.isConnected) return;
        var grid = this.querySelector('[data-sk-grid]');
        var freshGrid = fresh && fresh.querySelector('[data-sk-grid]');
        var freshPager = fresh && fresh.querySelector('[data-sk-pager]');
        if (!grid || !freshGrid || !freshPager) throw new Error('Unexpected page');
        this.withEmpire(() => {
          Array.prototype.forEach.call(freshGrid.children, (card) => grid.appendChild(document.importNode(card, true)));
          var pager = this.querySelector('[data-sk-pager]');
          if (pager) pager.replaceWith(document.importNode(freshPager, true));
        });
        var shown = this.querySelectorAll('[data-sk-grid] > .productgrid--item').length;
        this.querySelectorAll('[data-sk-shown]').forEach((el) => { el.textContent = shown.toLocaleString('en-US'); });
      } catch (error) {
        location.assign(href);
        return;
      } finally {
        this.loadingMore = false;
      }
      this.observe();
    }
  }

  customElements.define('sk-listing', SkListing);
})();
